import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { api, ApiError, clearAuth, getToken, messageFromBody } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { useAuth } from '../lib/auth';

/**
 * AS9100D §9.2 — Hibrit Tetkik Sayfası
 *
 * Rubric'ten seçili sorular agent'a koşulur → COMPLIANT / WEAK / NC / INSUFFICIENT
 * verdict'lerle taslak bulgular üretilir → KYT taslakları onaylayıp
 * AuditRecord.findings'e ekler.
 */

type Verdict = 'COMPLIANT' | 'WEAK' | 'NON_CONFORMITY' | 'INSUFFICIENT_DATA';
type DraftStatus = 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED';

/** AS9100D §9.2 çapraz kontrol kaynağı — rubric sorusu Mongo yerel koleksiyonuna mı yoksa MES'e mi bakıyor. */
type RubricCheck =
  | { source: 'mongo'; collection: string; mustExistWithin?: string; minCount: number }
  | { source: 'mes'; resource: string; mustExistWithin: string; minCount: number };

interface RubricQuestion {
  id: string;
  procedureCode: string;
  clause: string;
  question: string;
  expectedEvidence: string[];
  evidenceQuery: string;
  successCriteria: string;
  defaultSeverity: string;
  tags: string[];
  check?: RubricCheck;
}

interface EvidenceSnippet {
  docCode: string;
  clauses: string[];
  sourcePath: string;
  score: number;
  excerpt: string;
}

interface FindingDraft {
  _id: string;
  runId: string;
  questionId: string;
  procedureCode: string;
  clause: string;
  question: string;
  verdict: Verdict;
  rationale: string;
  evidence: EvidenceSnippet[];
  // source: Task 1'den önce yazılmış taslaklarda yok — o kayıtların hepsi mongo kaynaklıydı.
  mongoCheckResult: { source?: 'mongo' | 'mes'; collection: string; count: number; passed: boolean } | null;
  suggestedFinding: { category: string; description: string; evidence: string } | null;
  status: DraftStatus;
  createdAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
}

interface AuditRun {
  _id: string;
  scope: string;
  procedureCodes: string[];
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  startedAt: string;
  completedAt: string | null;
  triggeredBy: string;
  stats: { total: number; compliant: number; weak: number; nc: number; insufficient: number };
  progress: { done: number; total: number };
  error: string | null;
}

interface RubricResponse {
  items: RubricQuestion[];
  byProcedure: Record<string, RubricQuestion[]>;
  byClauseRoot: Record<string, RubricQuestion[]>;
  total: number;
}

interface StartRunResponse {
  runId: string;
  total: number;
}

interface RunDetailResponse {
  run: AuditRun;
  drafts: FindingDraft[];
}

type ProgressStreamEvent = {
  type: 'progress';
  done: number;
  total: number;
  questionId: string;
  verdict: Verdict;
};
type TerminalStreamEvent = { type: 'done'; stats: AuditRun['stats'] } | { type: 'error'; message: string };
type StreamEvent = ProgressStreamEvent | TerminalStreamEvent;

/**
 * SSE akışını okur; her `progress` event'inde `onProgress` çağrılır, akış
 * `done`/`error` ile kapanınca o event döner. `EventSource` Authorization
 * başlığı taşıyamadığı, api() de akış gövdesini vermediği için `fetch` +
 * `ReadableStream` kullanılır (bkz. AgentChatPage.tsx — aynı token ve hata
 * gövdesi deseni). HTTP hatasında gövdedeki mesajla (`messageFromBody`) Error
 * fırlatır; 401 api()'deki gibi oturumu da düşürür.
 *
 * Ping yorum satırları (`: ping`) `data: ` ile başlamadığı için atlanır.
 * Bağlantı hiç terminal event vermeden kapanırsa `null` döner — çağıran bunu
 * "durum belirsiz" sayıp geçmiş tablosuna yönlendirir. `signal` iptal edilirse
 * (unmount veya yeni bir izleme başlatıldığında) `fetch`/`reader.read()`
 * `AbortError` ile reddeder — çağıran bunu ayrıca ele alır.
 */
async function readAuditRunStream(
  runId: string,
  signal: AbortSignal,
  onProgress: (ev: ProgressStreamEvent) => void,
): Promise<TerminalStreamEvent | null> {
  const token = getToken();
  const res = await fetch(`/v1/audit-runs/${runId}/stream`, {
    signal,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    // Gövde okunamasa (bağlantı koptu, iptal) bile oturum düşsün diye gövdeden önce.
    if (res.status === 401) clearAuth();
    const raw = await res.text().catch(() => '');
    let body: unknown = raw;
    try {
      body = JSON.parse(raw);
    } catch {
      // düz metin gövde
    }
    throw new Error(messageFromBody(body, res.status));
  }
  if (!res.body) return null;

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    // SSE event'leri "\n\n" ile ayrılır; bir event iki chunk'a bölünürse yarım
    // kalan parça buffer'da bekletilip bir sonraki read()'de tamamlanır.
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';
    for (const part of parts) {
      const line = part.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue; // ': ping' yorumları atlanır
      const ev = JSON.parse(line.slice(6)) as StreamEvent;
      if (ev.type === 'progress') onProgress(ev);
      else return ev;
    }
  }
  return null;
}

interface AuditRecord {
  _id: string;
  auditNumber: string;
  scope: string;
  status: string;
}

const VERDICT_STYLE: Record<Verdict, { label: string; class: string }> = {
  COMPLIANT: {
    label: 'Uygun',
    class: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  },
  WEAK: {
    label: 'Zayıf',
    class: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  },
  NON_CONFORMITY: {
    label: 'Uygunsuzluk',
    class: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  },
  INSUFFICIENT_DATA: {
    label: 'Yetersiz Veri',
    class: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  },
};

export function AuditPage(): JSX.Element {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('audit.write');
  const qc = useQueryClient();

  const rubric = useQuery({
    queryKey: ['audit-rubric'],
    queryFn: () => api.get<RubricResponse>('/v1/audit-rubric'),
  });

  const runs = useQuery({
    queryKey: ['audit-runs'],
    queryFn: () => api.get<{ items: AuditRun[] }>('/v1/audit-runs'),
  });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [scope, setScope] = useState('İç Tetkik — Pilot');
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number; questionId?: string } | null>(
    null,
  );
  const [currentResult, setCurrentResult] = useState<RunDetailResponse | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<'procedure' | 'clauseRoot'>('clauseRoot');
  const [criticalOnly, setCriticalOnly] = useState(false);

  // Aynı anda tek koşu izlenir. Ref, async akış-okuma closure'ları içinde
  // "hâlâ bu koşuyu mu izliyoruz" diye karşılaştırmak için — state kendisi
  // closure oluştuğu andaki değerde donup kalır (bayatlar).
  const activeRunIdRef = useRef<string | null>(null);
  // Sayfadan ayrılınca veya yeni bir izleme başlayınca önceki fetch/SSE
  // bağlantısını gerçekten kapatır (AgentChatPage.tsx'teki abortRef deseniyle
  // aynı) — aksi halde sunucudaki abonelik (EventEmitter) koşu bitene kadar açık kalır.
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  /** Koşuyu izlemeye başlar: SSE ile ilerleme, bitince GET ile draft'lar. */
  function watchRun(runId: string, total: number, initialDone = 0): void {
    abortRef.current?.abort(); // önceki izleme sürüyorsa kapat
    const ac = new AbortController();
    abortRef.current = ac;
    activeRunIdRef.current = runId;
    setActiveRunId(runId);
    setProgress({ done: initialDone, total });
    setRunError(null);
    setCurrentResult(null);

    void (async () => {
      try {
        const finalEvent = await readAuditRunStream(runId, ac.signal, (ev) => {
          if (activeRunIdRef.current === runId) {
            setProgress({ done: ev.done, total: ev.total, questionId: ev.questionId });
          }
        });
        if (activeRunIdRef.current !== runId) return; // yeni bir izleme başladı, bu sonuç bayat

        if (!finalEvent || finalEvent.type === 'error') {
          setRunError(
            finalEvent?.type === 'error'
              ? finalEvent.message
              : 'Bağlantı koptu — koşu arka planda sürüyor olabilir, geçmiş tablosundan izleyin.',
          );
          return;
        }
        const result = await api.get<RunDetailResponse>(`/v1/audit-runs/${runId}`);
        if (activeRunIdRef.current === runId) setCurrentResult(result);
      } catch (err) {
        if ((err as Error).name === 'AbortError') return; // iptal edildi (unmount/yeni izleme) — sessizce çık
        if (activeRunIdRef.current === runId) {
          setRunError(err instanceof Error ? err.message : 'Akış okunamadı');
        }
      } finally {
        if (abortRef.current === ac) abortRef.current = null;
        if (activeRunIdRef.current === runId) {
          activeRunIdRef.current = null;
          setActiveRunId(null);
        }
        void qc.invalidateQueries({ queryKey: ['audit-runs'] });
      }
    })();
  }

  // Bitmiş koşunun taslakları: koşu KYT başka sayfadayken bitmişse sonuç kartı hiç
  // dolmaz — geçmiş satırından yeniden açılabilmeli (insan incelemesi modülün amacı).
  const openRun = useMutation({
    mutationFn: (runId: string) => api.get<RunDetailResponse>(`/v1/audit-runs/${runId}`),
    onSuccess: (result) => {
      setRunError(null);
      setCurrentResult(result);
    },
  });

  const startRun = useMutation({
    mutationFn: () =>
      api.post<StartRunResponse>('/v1/audit-runs', {
        scope,
        questionIds: [...selectedIds],
      }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['audit-runs'] });
      watchRun(data.runId, data.total);
    },
  });

  const sourceGroups =
    groupBy === 'procedure' ? rubric.data?.byProcedure : rubric.data?.byClauseRoot;
  const grouped = useMemo(() => {
    const src = sourceGroups ?? {};
    if (!criticalOnly) return src;
    const out: Record<string, RubricQuestion[]> = {};
    for (const [k, qs] of Object.entries(src)) {
      const f = qs.filter((q) => q.defaultSeverity !== 'OBSERVATION');
      if (f.length > 0) out[k] = f;
    }
    return out;
  }, [sourceGroups, criticalOnly]);

  const procCodes = useMemo(() => {
    const keys = Object.keys(grouped);
    if (groupBy === 'clauseRoot') return keys.sort((a, b) => Number(a) - Number(b));
    return keys.sort();
  }, [grouped, groupBy]);

  const allCount = rubric.data?.total ?? 0;
  const visibleCount = useMemo(
    () => Object.values(grouped).reduce((s, qs) => s + qs.length, 0),
    [grouped],
  );
  const selCount = selectedIds.size;

  const selectVisible = () => {
    setSelectedIds(new Set(Object.values(grouped).flatMap((qs) => qs.map((q) => q.id))));
  };
  const clearSelection = () => setSelectedIds(new Set());

  const toggleQ = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleProcedure = (code: string) => {
    const qs = grouped[code] ?? [];
    const allSelected = qs.every((q) => selectedIds.has(q.id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) qs.forEach((q) => next.delete(q.id));
      else qs.forEach((q) => next.add(q.id));
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="KYS Tetkik (Agent)"
        subtitle="AS9100D §9.2 — Rubric tabanlı hibrit denetim. Agent rubric sorularını RAG kanıtı ve Mongo/MES kayıt kontrolüyle değerlendirir, taslak bulgu üretir; KYT onaylar."
      />

      {/* === Rubric Seçimi === */}
      <section className="card p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Rubric Soruları
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {allCount} toplam · Görünür: {visibleCount} ({procCodes.length}{' '}
              {groupBy === 'procedure' ? 'prosedür' : 'AS9100 madde grubu'}) · Seçili: {selCount}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              placeholder="Tetkik kapsamı (örn. 2026-Q2 İç Tetkik)"
              className="input w-72"
            />
            {activeRunId ? (
              <RunProgress progress={progress} />
            ) : (
              <button
                type="button"
                disabled={
                  !canWrite || selCount === 0 || startRun.isPending || scope.trim().length === 0
                }
                onClick={() => startRun.mutate()}
                className="btn-primary disabled:opacity-50"
              >
                {startRun.isPending ? 'Başlatılıyor...' : `Tetkiki Başlat (${selCount})`}
              </button>
            )}
          </div>
        </div>
        {!canWrite && (
          <div className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
            Tetkik başlatma yetkisi yok (audit.write izni gerekli).
          </div>
        )}
        <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-slate-200 pb-3 dark:border-slate-800">
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200 text-xs dark:border-slate-700">
            <button
              type="button"
              onClick={() => setGroupBy('clauseRoot')}
              className={clsx(
                'px-3 py-1.5 transition',
                groupBy === 'clauseRoot'
                  ? 'bg-brand-600 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800',
              )}
            >
              AS9100 Maddesi
            </button>
            <button
              type="button"
              onClick={() => setGroupBy('procedure')}
              className={clsx(
                'border-l border-slate-200 px-3 py-1.5 transition dark:border-slate-700',
                groupBy === 'procedure'
                  ? 'bg-brand-600 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800',
              )}
            >
              Prosedür
            </button>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={criticalOnly}
              onChange={(e) => setCriticalOnly(e.target.checked)}
              className="h-4 w-4"
            />
            Sadece kritik (MAJOR/MINOR/OFI)
          </label>
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={selectVisible} className="btn-secondary text-xs">
              Görünenleri seç ({visibleCount})
            </button>
            <button type="button" onClick={clearSelection} className="btn-secondary text-xs">
              Temizle
            </button>
          </div>
        </div>
        {rubric.isLoading && (
          <p className="text-sm text-slate-500">Rubric yükleniyor...</p>
        )}
        {rubric.error && (
          <p className="text-sm text-rose-600">Hata: {(rubric.error as Error).message}</p>
        )}
        <div className="space-y-3">
          {procCodes.map((code) => {
            const qs = grouped[code] ?? [];
            const allSel = qs.every((q) => selectedIds.has(q.id));
            const someSel = qs.some((q) => selectedIds.has(q.id));
            return (
              <details
                key={code}
                open
                className="rounded-md border border-slate-200 dark:border-slate-800"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-2 px-4 py-2.5 text-sm font-medium text-slate-900 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-800/50">
                  <span className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={allSel}
                      ref={(el) => {
                        if (el) el.indeterminate = !allSel && someSel;
                      }}
                      onChange={() => toggleProcedure(code)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4"
                    />
                    <span>
                      {groupBy === 'clauseRoot' ? `AS9100D §${code}` : code} — {qs.length} soru
                    </span>
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {groupBy === 'clauseRoot' ? qs[0]?.procedureCode : `§${qs[0]?.clause}`}
                  </span>
                </summary>
                <ul className="divide-y divide-slate-100 px-4 py-2 dark:divide-slate-800">
                  {qs.map((q) => (
                    <li key={q.id} className="flex items-start gap-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(q.id)}
                        onChange={() => toggleQ(q.id)}
                        className="mt-0.5 h-4 w-4"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-slate-800 dark:text-slate-200">
                          {q.id} · §{q.clause}
                        </div>
                        <div className="text-slate-600 dark:text-slate-400">{q.question}</div>
                        <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
                          <span className="badge bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                            {q.defaultSeverity}
                          </span>
                          {q.check && (
                            <span className="badge bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200">
                              {q.check.source === 'mongo'
                                ? `↻ ${q.check.collection}`
                                : `↻ MES · ${q.check.resource}`}
                            </span>
                          )}
                          {q.tags.map((t) => (
                            <span
                              key={t}
                              className="badge bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                            >
                              #{t}
                            </span>
                          ))}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </details>
            );
          })}
        </div>
        {startRun.isError && (
          <p className="mt-3 text-sm text-rose-600">
            Tetkik başlatılamadı: {(startRun.error as ApiError).message}
          </p>
        )}
        {/* "tamamlanamadı" değil: bu slot koşu sürerken de dolabiliyor (akış bu süreçten izlenemiyorsa). */}
        {runError && (
          <p className="mt-3 text-sm text-rose-600">Tetkik koşusu izlenemedi: {runError}</p>
        )}
      </section>

      {/* === Mevcut Sonuçlar === */}
      {currentResult && <RunResultCard result={currentResult} onChanged={() => setCurrentResult(null)} canWrite={canWrite} />}

      {/* === Geçmiş Runlar === */}
      <section className="card p-5">
        <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">
          Geçmiş Tetkik Koşuları
        </h2>
        {runs.isLoading && <p className="text-sm text-slate-500">Yükleniyor...</p>}
        {runs.data && runs.data.items.length === 0 && (
          <p className="text-sm text-slate-500">Henüz tetkik koşusu yok.</p>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <th className="py-2 pr-3">Run #</th>
                <th className="py-2 pr-3">Kapsam</th>
                <th className="py-2 pr-3">Prosedürler</th>
                <th className="py-2 pr-3">Durum</th>
                <th className="py-2 pr-3">İstatistik</th>
                <th className="py-2 pr-3">Tarih</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {(runs.data?.items ?? []).map((r) => {
                // .lean() okuması zod'dan geçmiyor: progress'siz eski kayıt render'ı patlatırdı.
                const prog = r.progress ?? { done: 0, total: 0 };
                return (
                <tr key={r._id}>
                  <td className="py-2 pr-3 font-mono text-xs text-slate-700 dark:text-slate-300">
                    {r._id.slice(-10)}
                  </td>
                  <td className="py-2 pr-3 text-slate-900 dark:text-slate-100">{r.scope}</td>
                  <td className="py-2 pr-3 text-slate-700 dark:text-slate-300">
                    {r.procedureCodes.join(', ')}
                  </td>
                  <td className="py-2 pr-3">
                    <span
                      className={clsx(
                        'badge',
                        r.status === 'COMPLETED'
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                          : r.status === 'RUNNING'
                            ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'
                            : 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
                      )}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-xs text-slate-600 dark:text-slate-400">
                    {r.status === 'RUNNING' ? (
                      <div className="flex items-center gap-2">
                        <span>
                          {prog.done}/{prog.total}
                        </span>
                        <button
                          type="button"
                          disabled={activeRunId !== null}
                          onClick={() => watchRun(r._id, prog.total, prog.done)}
                          className="btn-secondary px-2 py-0.5 text-[11px] disabled:opacity-50"
                        >
                          İzle
                        </button>
                      </div>
                    ) : r.status === 'FAILED' ? (
                      <span className="text-rose-600 dark:text-rose-400">
                        {r.error ?? 'Bilinmeyen hata'}
                      </span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span>
                          ✓{r.stats.compliant} · ◐{r.stats.weak} · ✗{r.stats.nc} · ?{r.stats.insufficient}
                        </span>
                        <button
                          type="button"
                          disabled={openRun.isPending}
                          onClick={() => openRun.mutate(r._id)}
                          className="btn-secondary px-2 py-0.5 text-[11px] disabled:opacity-50"
                        >
                          Sonuçları Aç
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs text-slate-500 dark:text-slate-400">
                    {new Date(r.startedAt).toLocaleString('tr-TR')}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {openRun.isError && (
          <p className="mt-3 text-sm text-rose-600">
            Sonuçlar açılamadı: {(openRun.error as ApiError).message}
          </p>
        )}
      </section>
    </div>
  );
}

function RunProgress({
  progress,
}: {
  progress: { done: number; total: number; questionId?: string } | null;
}): JSX.Element {
  const done = progress?.done ?? 0;
  const total = progress?.total ?? 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="w-72">
      <div className="mb-1 flex items-center justify-between text-xs text-slate-600 dark:text-slate-400">
        <span>
          Çalışıyor... {done}/{total}
        </span>
        {progress?.questionId && <span className="font-mono">{progress.questionId}</span>}
      </div>
      <div
        role="progressbar"
        aria-label="Tetkik ilerlemesi"
        aria-valuenow={done}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuetext={`${done} / ${total}`}
        className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800"
      >
        <div
          className="h-full rounded-full bg-brand-600 transition-all dark:bg-brand-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function RunResultCard({
  result,
  onChanged,
  canWrite,
}: {
  result: RunDetailResponse;
  onChanged: () => void;
  canWrite: boolean;
}): JSX.Element {
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <section className="card p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Sonuçlar — {result.run.scope}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {result.run.stats.total} soru · Uygun {result.run.stats.compliant} · Zayıf{' '}
            {result.run.stats.weak} · Uygunsuzluk {result.run.stats.nc} · Yetersiz{' '}
            {result.run.stats.insufficient}
          </p>
        </div>
        <button onClick={onChanged} className="btn-secondary text-xs">
          Kapat
        </button>
      </div>
      <ul className="divide-y divide-slate-200 dark:divide-slate-800">
        {result.drafts.map((d) => (
          <li key={d._id} className="py-3">
            <button
              onClick={() => setOpenId(openId === d._id ? null : d._id)}
              className="flex w-full items-start justify-between gap-3 text-left"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-900 dark:text-slate-100">
                  <span>{d.questionId}</span>
                  <span className="text-xs text-slate-500">§{d.clause}</span>
                </div>
                <div className="mt-0.5 text-sm text-slate-600 dark:text-slate-400">
                  {d.question}
                </div>
              </div>
              <span className={clsx('badge shrink-0', VERDICT_STYLE[d.verdict].class)}>
                {VERDICT_STYLE[d.verdict].label}
              </span>
            </button>
            {openId === d._id && (
              <DraftDetail draft={d} canWrite={canWrite} />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function DraftDetail({ draft, canWrite }: { draft: FindingDraft; canWrite: boolean }): JSX.Element {
  const qc = useQueryClient();
  const audits = useQuery({
    queryKey: ['audits'],
    queryFn: () => api.get<{ items: AuditRecord[] }>('/v1/audits'),
    enabled: draft.suggestedFinding !== null,
  });
  const [auditId, setAuditId] = useState<string>('');
  const [status, setStatus] = useState<DraftStatus>(draft.status);

  const approve = useMutation({
    mutationFn: () =>
      api.post(`/v1/audit-finding-drafts/${draft._id}/approve`, { auditId }),
    onSuccess: () => {
      setStatus('APPROVED');
      void qc.invalidateQueries({ queryKey: ['audit-runs'] });
    },
  });
  const reject = useMutation({
    mutationFn: () => api.post(`/v1/audit-finding-drafts/${draft._id}/reject`),
    onSuccess: () => setStatus('REJECTED'),
  });

  return (
    <div className="mt-3 rounded-md bg-slate-50 p-3 text-sm dark:bg-slate-900/50">
      <p className="text-slate-800 dark:text-slate-200">
        <span className="font-semibold">Gerekçe:</span> {draft.rationale}
      </p>
      {draft.mongoCheckResult && (
        <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
          {draft.mongoCheckResult.source === 'mes' ? 'MES' : 'Mongo'} çapraz kontrol —{' '}
          <code>{draft.mongoCheckResult.collection}</code>: {draft.mongoCheckResult.count} kayıt ·{' '}
          {draft.mongoCheckResult.passed ? '✓ geçti' : '✗ başarısız'}
        </p>
      )}
      {draft.suggestedFinding && (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm dark:border-rose-900 dark:bg-rose-900/20">
          <div className="font-semibold text-rose-900 dark:text-rose-200">
            Önerilen bulgu — {draft.suggestedFinding.category}
          </div>
          <div className="mt-1 text-rose-800 dark:text-rose-300">
            {draft.suggestedFinding.description}
          </div>
          {draft.suggestedFinding.evidence && (
            <div className="mt-1 text-xs text-rose-700/70 dark:text-rose-400/70">
              Kanıt: {draft.suggestedFinding.evidence}
            </div>
          )}
        </div>
      )}
      {draft.evidence.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs font-medium text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200">
            RAG Kanıtları ({draft.evidence.length})
          </summary>
          <ul className="mt-2 space-y-2">
            {draft.evidence.map((e, i) => (
              <li
                key={i}
                className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-900"
              >
                <div className="flex flex-wrap items-center gap-2 text-slate-600 dark:text-slate-400">
                  <span className="font-mono">{e.docCode}</span>
                  <span>skor {e.score.toFixed(3)}</span>
                  <span className="truncate font-mono text-[10px]">{e.sourcePath}</span>
                </div>
                <p className="mt-1 text-slate-700 dark:text-slate-300">{e.excerpt}</p>
              </li>
            ))}
          </ul>
        </details>
      )}
      {draft.suggestedFinding && status === 'PENDING_REVIEW' && canWrite && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-3 dark:border-slate-700">
          <select
            value={auditId}
            onChange={(e) => setAuditId(e.target.value)}
            className="input w-64"
          >
            <option value="">Bulguyu hangi tetkike ekle?</option>
            {(audits.data?.items ?? []).map((a) => (
              <option key={a._id} value={a._id}>
                {a.auditNumber} — {a.scope}
              </option>
            ))}
          </select>
          <button
            onClick={() => approve.mutate()}
            disabled={!auditId || approve.isPending}
            className="btn-primary text-xs disabled:opacity-50"
          >
            {approve.isPending ? 'Onaylanıyor...' : 'Onayla & Ekle'}
          </button>
          <button
            onClick={() => reject.mutate()}
            disabled={reject.isPending}
            className="btn-secondary text-xs"
          >
            Reddet
          </button>
          {(approve.error || reject.error) && (
            <span className="text-xs text-rose-600">
              {((approve.error || reject.error) as Error).message}
            </span>
          )}
        </div>
      )}
      {status !== 'PENDING_REVIEW' && (
        <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          Durum: <span className="font-medium">{status}</span>
        </div>
      )}
    </div>
  );
}
